import { TextNode } from "lexical";

/**
 * A TextNode extension that carries text-level inline styles:
 *   - color      (text colour)
 *   - fontSize
 *
 * backgroundColor is intentionally excluded — it belongs on the table cell,
 * not the text run. Setting it here causes the entire text to appear with a
 * coloured background highlight rather than the cell being shaded.
 *
 * These styles are preserved in the HTML output as inline `style` attributes
 * so they survive the round-trip:
 *   Lexical → HTML string → C# HtmlToOpenXml → Word doc
 */
export class StyledTextNode extends TextNode {
  /** @type {string|null} */ __color;
  /** @type {string|null} */ __fontSize;

    static getType() {
        return "styled-text";
    }

    static clone(node) {
        return new StyledTextNode(
            node.__text,
            {
                color: node.__color,
                fontSize: node.__fontSize,
            },
            node.__key
        );
    }

    /**
     * @param {string} text
     * @param {{ color?: string|null, fontSize?: string|null }} styles
     * @param {string} [key]
     */
    constructor(text, styles = {}, key) {
        super(text, key);
        this.__color = styles.color ?? null;
        this.__fontSize = styles.fontSize ?? null;
    }

    // ── DOM creation (editor rendering) ─────────────────────────────────────────

    createDOM(config) {
        const dom = super.createDOM(config);
        this._applyStyles(dom);
        return dom;
    }

    updateDOM(prevNode, dom, config) {
        const updated = super.updateDOM(prevNode, dom, config);
        this._applyStyles(dom);
        return updated;
    }

    _applyStyles(el) {
        if (this.__color) el.style.color = this.__color;
        if (this.__fontSize) el.style.fontSize = this.__fontSize;
    }

    /**
     * exportDOM is called by $generateHtmlFromNodes to produce the HTML string
     * stored as the field value and sent to the C# backend.
     */
    exportDOM(editor) {
        const { element } = super.exportDOM(editor);
        const span = document.createElement("span");
        if (this.__color) span.style.color = this.__color;
        if (this.__fontSize) span.style.fontSize = this.__fontSize;
        span.appendChild(element);
        return { element: span };
    }

    // ── JSON serialisation (editor state persistence) ────────────────────────────

    exportJSON() {
        return {
            ...super.exportJSON(),
            type: "styled-text",
            color: this.__color,
            fontSize: this.__fontSize,
        };
    }

    static importJSON(serialisedNode) {
        const node = new StyledTextNode(serialisedNode.text, {
            color: serialisedNode.color,
            fontSize: serialisedNode.fontSize,
        });
        node.setFormat(serialisedNode.format);
        node.setDetail(serialisedNode.detail);
        node.setMode(serialisedNode.mode);
        node.setStyle(serialisedNode.style);
        return node;
    }

    // ── DOM import (used by $generateNodesFromDOM) ────────────────────────────────

    static importDOM() {
        return {
            span: (node) => {
                const style = node.style;
                if (!style.color && !style.fontSize) return null;
                return {
                    conversion: (domNode) => {
                        const styles = {
                            color: domNode.style.color || null,
                            fontSize: domNode.style.fontSize || null,
                        };
                        const text = domNode.textContent ?? "";
                        return { node: new StyledTextNode(text, styles) };
                    },
                    priority: 1,
                };
            },
        };
    }
}

// ── Factory helpers ───────────────────────────────────────────────────────────

/**
 * @param {string} text
 * @param {{ color?: string|null, fontSize?: string|null }} styles
 */
export function $createStyledTextNode(text, styles = {}) {
    return new StyledTextNode(text, styles);
}

export function $isStyledTextNode(node) {
    return node instanceof StyledTextNode;
}